import { useCallback, useEffect, useState } from 'react';
import type { PackItem } from '../Home/DynamicHomepage/components/NewLayout/RafflePage/RafflePage';
import type { PromotionRewardsType } from '../../types/types';

/* The promo pack — `/promo`, its confirmation page, and the two pieces of
 * it the member portal shows (the headline and the rewards lines).
 *
 * ONE PUBLIC READ. `GET /package/promotion` is asked with a bare fetch and no
 * credentials, the way CardCatalog asks for its public list: the page is for
 * people who are not signed in, `useNetwork()` would bring its 401 → refresh →
 * logout cycle along for a read that needs none, and a cookie would only tie a
 * public read to whoever happens to be signed in. A 404 is an ANSWER — there is
 * no promo pack this week — not an error.
 *
 * NOTHING HERE PRICES A CHARGE. The checkout sends the package id and
 * `hasBonus`, and the server prices the intent from its own row. What this file
 * works out is what the pages SAY — the receipt's cards, entries and amount —
 * and that has to be what the server charges: the price, plus the bonus price
 * when the bonus was ticked, as `RafflePaymentPage.getDisplayedCheckoutPrice`
 * does for a one-off pack.
 */

/** `GET /package/promotion`, as the API sends it. */
export type ApiPromotion = {
  id: number | string;
  name: string;
  type?: string;
  description?: string | null;
  imageUrl?: string | null;
  price: number | string;
  cards?: number | string | null;
  entries?: number | string | null;
  Bonus?: {
    description?: string | null;
    extraCards?: number | string | null;
    extraEntries?: number | string | null;
    price?: number | string | null;
  } | null;
  rewards?: Partial<PromotionRewardsType> | null;
  promoDraw?: {
    id?: number | string;
    name?: string | null;
    endDate?: string | null;
  } | null;
};

const text = (v: unknown) => (typeof v === 'string' ? v.trim() : '');

/** The body as a promotion, or null when it is not one a page can sell: it must
 * name the package and carry a price above zero. */
export const promotionFrom = (body: unknown): ApiPromotion | null => {
  const p = body as Partial<ApiPromotion> | null;
  if (!p || typeof p !== 'object' || Array.isArray(p)) return null;
  if (p.id === undefined || p.id === null || p.id === '') return null;
  if (!text(p.name)) return null;
  const price = Number(String(p.price ?? '').replace(/^\$/, ''));
  if (!Number.isFinite(price) || price <= 0) return null;
  return p as ApiPromotion;
};

/* The pack as the checkout takes it — exactly the mapping `RafflePage` gives
 * its one-off packs (`toPackStandaloneItems`), so the form's "Bonus add-on"
 * toggle shows the same numbers it does on /giveaway. `dotColor` is unused by
 * the checkout. */
export const promoPackItem = (p: ApiPromotion): PackItem => ({
  id: String(p.id),
  title: p.name,
  price:
    typeof p.price === 'number'
      ? p.price.toFixed(2)
      : String(p.price).replace(/^\$/, ''),
  entries: Number(p.entries || 0),
  nfts: Number(p.cards || 0),
  description: p.description || '',
  dotColor: '',
  bonus: p.Bonus
    ? {
        description: p.Bonus.description || '',
        extraCards: Number(p.Bonus.extraCards || 0),
        extraEntries: Number(p.Bonus.extraEntries || 0),
        price: Number(p.Bonus.price || 0)
      }
    : undefined
});

/* ── this week's rewards ─────────────────────────────────────────────────── */

/* They belong to the promo DRAW that is running, not to the pack: INC tokens,
 * packs or both, plus one free-text line. A draw resets them, and until the
 * admin sets the next week's there are none — there is NO default, and nothing
 * here invents one. `rewards` is null when no draw is open or nothing is set,
 * and `announced` is false until one of the three is; every surface treats the
 * two alike, as "Rewards to be announced". A headline, blurb or picture can be
 * set while the rewards are still to be announced.
 *
 * They are what the week's draw offers, not what a purchase grants: nothing
 * that prints them may say they have been given or credited. */

type Rewards = Partial<PromotionRewardsType> | null;

/** The rewards taken apart, each part checked: the tokens (a positive amount),
 * the pack reward (a positive whole count of a named pack, with the pack's own
 * art when it has some) and the custom line. Every part is null/'' until the
 * API says the rewards are announced. The promo page draws these as tiles —
 * tokens on the left, the pack with its art on the right, the custom line
 * beneath; `rewardLines` words the same parts for everywhere else. */
export type RewardParts = {
  tokens: number | null;
  packs: { count: number; name: string; imageUrl: string | null } | null;
  custom: string;
};

export const rewardParts = (rewards?: Rewards): RewardParts => {
  if (rewards?.announced !== true) return { tokens: null, packs: null, custom: '' };
  const tokens = Number(rewards.tokens);
  const count = Number(rewards.packs?.count);
  const pack = text(rewards.packs?.name);
  const art = text(rewards.packs?.imageUrl);
  return {
    tokens: Number.isFinite(tokens) && tokens > 0 ? tokens : null,
    packs:
      Number.isInteger(count) && count > 0 && pack
        ? { count, name: pack, imageUrl: /^https:\/\//i.test(art) ? art : null }
        : null,
    custom: text(rewards.custom)
  };
};

/** The reward lines every surface prints, in this order — tokens, packs, then
 * the custom line: `['250 INC tokens', '2 × Relic Pack', 'Signed Spring Royale
 * jersey']`. A part that is missing, zero or blank is left out, and nothing is
 * listed until the API says the rewards are announced. */
export const rewardLines = (rewards?: Rewards): string[] => {
  const { tokens, packs, custom } = rewardParts(rewards);
  return [
    tokens !== null ? plural(tokens, 'INC token', 'INC tokens') : '',
    packs ? `${packs.count.toLocaleString('en-AU')} × ${packs.name}` : '',
    custom
  ].filter(Boolean);
};

/** Whether the rewards are announced, and so listed rather than "Rewards to be
 * announced": the API has to say so AND there has to be a line to show. */
export const rewardsAnnounced = (rewards?: Rewards) =>
  rewardLines(rewards).length > 0;

/** This week's headline, or '' when the admin wrote none. */
export const promoHeadline = (rewards?: Rewards) => text(rewards?.headline);

/** What the page shows: the admin's headline and blurb where there are some,
 * else the package's own name and description; the admin's picture — the
 * page's focus when there is one, and never the pack's own image, which sits
 * small in "What's inside"; and the reward lines, empty until announced. */
/* The package's own description, unless it is the placeholder the packages were
 * created with — the literal word "Description" — which is not copy to print. */
const packDescription = (v: unknown) => {
  const d = text(v);
  return /^description$/i.test(d) ? '' : d;
};

export const promoCopy = (p: ApiPromotion) => ({
  imageUrl: text(p.rewards?.imageUrl),
  packImageUrl: text(p.imageUrl),
  headline: promoHeadline(p.rewards) || text(p.name),
  blurb: text(p.rewards?.blurb) || packDescription(p.description),
  rewards: rewardLines(p.rewards)
});

/* Past 30 days the promo pages print the draw's date instead of a timer. The
 * bound began as a workaround — GiveawayCountdownTimer took its days from
 * `intervalToDuration` counted from the epoch, so a draw 35 days out read
 * "04 Days" — and the timers count whole days now (utils/countdownParts), so
 * it is only a choice of presentation: a date reads better than a five-week
 * countdown. The monthly A$10,000 draw can be 35 days out. */
export const TIMER_MAX_MS = 30 * 24 * 60 * 60 * 1000;

export type DrawWhen =
  | { kind: 'countdown'; at: Date }
  | { kind: 'date'; at: Date }
  | { kind: 'past' }
  | { kind: 'none' };

/** How a draw date can honestly be shown: a countdown, a date, closed, or not
 * announced. The timer has no closed state — it sits on 00:00:00:00 — so a past
 * date is never handed to it. */
export const drawWhen = (
  iso: string | null | undefined,
  now: number = Date.now()
): DrawWhen => {
  if (!iso) return { kind: 'none' };
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return { kind: 'none' };
  const left = at.getTime() - now;
  if (left <= 0) return { kind: 'past' };
  return left < TIMER_MAX_MS ? { kind: 'countdown', at } : { kind: 'date', at };
};

/** "Friday 30 October, 10:00 pm", in the visitor's own time zone. */
export const formatDrawDate = (at: Date) =>
  at.toLocaleString('en-AU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: 'numeric',
    minute: '2-digit'
  });

/** "A$29.95" — always two decimals. */
export const formatAud = (n: number) =>
  `A$${(Number.isFinite(n) ? n : 0).toFixed(2)}`;

/** "1 entry" / "30 entries". */
export const plural = (n: number, one: string, many: string) =>
  `${n.toLocaleString('en-AU')} ${n === 1 ? one : many}`;

/* ── the receipt the confirmation page reads ────────────────────────────── */

/* Written once the card has gone through, read by `/promo/confirmation`,
 * and the courtesy gate on that page: no receipt, no page. It grants nothing —
 * entries and cards come from the server's webhook, never from here.
 *
 * Only the name and email are kept from the details typed at checkout: that is
 * all the page prints, and the phone and address have no business sitting in
 * storage. sessionStorage, so it goes with the tab. */
export type PromoPackReceipt = {
  entry: { firstName: string; lastName: string; email: string };
  pack: PackItem;
  bonusSelected: boolean;
  /* The one-click boost taken after paying (the pack's Upsell option), or
     null: its extras and price are counted in alongside the pack's. */
  upsell: { extraEntries: number; extraCards: number; price: number } | null;
  amountPaidAud: number;
  accountCreated: boolean;
  paymentIntentId: string | null;
  purchasedAt: string;
};

const RECEIPT_KEY = 'promoPackConfirmation';

export const promoPackReceipt = ({
  entry,
  pack,
  bonusSelected,
  accountCreated,
  paymentIntentId,
  now = new Date()
}: {
  entry: { firstName?: string; lastName?: string; email?: string };
  pack: PackItem;
  bonusSelected?: boolean;
  accountCreated?: boolean;
  paymentIntentId?: string;
  now?: Date;
}): PromoPackReceipt => {
  /* The form reports the toggle only for a pack that HAS a bonus; this keeps
     the receipt honest even if that ever changes. */
  const withBonus = Boolean(bonusSelected && pack.bonus);
  const paid =
    Number(pack.price || 0) + (withBonus ? Number(pack.bonus?.price || 0) : 0);

  return {
    entry: {
      firstName: (entry.firstName || '').trim(),
      lastName: (entry.lastName || '').trim(),
      email: (entry.email || '').trim().toLowerCase()
    },
    pack,
    bonusSelected: withBonus,
    upsell: null,
    /* Cents, so 29.95 + 10 prints as 39.95 and never 39.949999. */
    amountPaidAud: Math.round(paid * 100) / 100,
    accountCreated: Boolean(accountCreated),
    paymentIntentId: paymentIntentId ?? null,
    purchasedAt: now.toISOString()
  };
};

/** The receipt once the boost offered after paying was taken: its price is
 * added to what was paid, and its extras count in the totals. */
export const withUpsell = (
  r: PromoPackReceipt,
  upsell: { extraEntries: number; extraCards: number; price: number }
): PromoPackReceipt => ({
  ...r,
  upsell: {
    extraEntries: Number(upsell.extraEntries) || 0,
    extraCards: Number(upsell.extraCards) || 0,
    price: Number(upsell.price) || 0
  },
  amountPaidAud: Math.round((r.amountPaidAud + (Number(upsell.price) || 0)) * 100) / 100
});

/** What the receipt lists: the pack's cards and entries, plus the bonus's when
 * it was added and the boost's when it was taken — the same sums the server's
 * webhooks award. */
export const receiptTotals = (r: PromoPackReceipt) => ({
  cards:
    Number(r.pack.nfts || 0) +
    (r.bonusSelected ? Number(r.pack.bonus?.extraCards || 0) : 0) +
    Number(r.upsell?.extraCards || 0),
  entries:
    Number(r.pack.entries || 0) +
    (r.bonusSelected ? Number(r.pack.bonus?.extraEntries || 0) : 0) +
    Number(r.upsell?.extraEntries || 0)
});

/** A stored or router-state receipt, checked into shape; null when it is not one. */
export const promoPackReceiptFrom = (
  value: unknown
): PromoPackReceipt | null => {
  const r = value as Partial<PromoPackReceipt> | null;
  if (!r || typeof r !== 'object') return null;
  if (!r.pack || typeof r.pack.title !== 'string' || !r.pack.title) return null;
  if (!r.entry || typeof r.entry.email !== 'string') return null;
  const amount = Number(r.amountPaidAud);
  if (!Number.isFinite(amount)) return null;
  return {
    entry: {
      firstName: String(r.entry.firstName ?? ''),
      lastName: String(r.entry.lastName ?? ''),
      email: r.entry.email
    },
    pack: r.pack,
    bonusSelected: Boolean(r.bonusSelected),
    upsell:
      r.upsell && typeof r.upsell === 'object'
        ? {
            extraEntries: Number(r.upsell.extraEntries) || 0,
            extraCards: Number(r.upsell.extraCards) || 0,
            price: Number(r.upsell.price) || 0
          }
        : null,
    amountPaidAud: amount,
    accountCreated: Boolean(r.accountCreated),
    paymentIntentId: r.paymentIntentId ?? null,
    purchasedAt: String(r.purchasedAt ?? '')
  };
};

export const savePromoPackReceipt = (receipt: PromoPackReceipt) => {
  try {
    sessionStorage.setItem(RECEIPT_KEY, JSON.stringify(receipt));
  } catch {
    /* Private browsing, or a full quota: the navigation carries the receipt in
       router state as well, so the confirmation page still renders. */
  }
};

export const readPromoPackReceipt = (): PromoPackReceipt | null => {
  try {
    const raw = sessionStorage.getItem(RECEIPT_KEY);
    return raw ? promoPackReceiptFrom(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
};

/* ── the two public reads ───────────────────────────────────────────────── */

const apiBase = () => (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

export type PromotionLoad =
  | { status: 'ready'; promotion: ApiPromotion }
  | { status: 'none' }
  | { status: 'error' };

/** The live promotion. Never rejects: 404 → `none`, anything else that is not
 * a sellable promotion → `error`. No `REACT_APP_API_URL` is an error, never a
 * request to the page's own origin. */
export const fetchPromotion = async (
  signal?: AbortSignal
): Promise<PromotionLoad> => {
  const base = apiBase();
  if (!base) return { status: 'error' };
  try {
    const res = await fetch(`${base}/package/promotion`, {
      credentials: 'omit',
      signal
    });
    if (res.status === 404) return { status: 'none' };
    if (!res.ok) return { status: 'error' };
    const promotion = promotionFrom(await res.json());
    return promotion ? { status: 'ready', promotion } : { status: 'error' };
  } catch {
    return { status: 'error' };
  }
};

export type Jackpot = { endDate: string | null; prizeAmount: number | null };

/** The A$10,000 draw: `GET /giveaway/offer` with no code — the date and the
 * prize, the same endpoint the QR pages read. Null when it cannot be had. */
export const fetchJackpot = async (
  signal?: AbortSignal
): Promise<Jackpot | null> => {
  const base = apiBase();
  if (!base) return null;
  try {
    const res = await fetch(`${base}/giveaway/offer`, {
      credentials: 'omit',
      signal
    });
    if (!res.ok) return null;
    const body = await res.json();
    const prize = Number(body?.prizeAmount);
    return {
      endDate: typeof body?.endDate === 'string' ? body.endDate : null,
      prizeAmount: Number.isFinite(prize) && prize > 0 ? prize : null
    };
  } catch {
    return null;
  }
};

/* ── previous promo winners ─────────────────────────────────────────────── */

/** One past draw as GET /package/promotion/winners sends it: who won (first
 * name and initial only) and what that week rewarded. */
export type PromoWinnerDraw = {
  id: number;
  name: string | null;
  endDate: string;
  winners: { firstName: string; lastInitial: string }[];
  rewards: Partial<PromotionRewardsType> | null;
};

/** The last promo draws' winners, newest first; null when they cannot be had. */
export const fetchPromoWinners = async (
  signal?: AbortSignal
): Promise<PromoWinnerDraw[] | null> => {
  const base = apiBase();
  if (!base) return null;
  try {
    const res = await fetch(`${base}/package/promotion/winners`, {
      credentials: 'omit',
      signal
    });
    if (!res.ok) return null;
    const body = await res.json();
    const draws = Array.isArray(body?.draws) ? body.draws : null;
    if (!draws) return null;
    return draws
      .filter((d: Partial<PromoWinnerDraw>) => d && Array.isArray(d.winners) && d.winners.length)
      .map((d: PromoWinnerDraw) => ({
        id: Number(d.id),
        name: typeof d.name === 'string' ? d.name : null,
        endDate: String(d.endDate ?? ''),
        winners: d.winners.map((w) => ({
          firstName: text(w?.firstName),
          lastInitial: text(w?.lastInitial).slice(0, 1).toUpperCase()
        })),
        rewards: d.rewards ?? null
      }));
  } catch {
    return null;
  }
};

/** Previous winners for a page: undefined while they load, then the list
 * (null when it cannot be had). Read once. */
export const usePromoWinners = () => {
  const [draws, setDraws] = useState<PromoWinnerDraw[] | null | undefined>(undefined);
  useEffect(() => {
    const ctrl = new AbortController();
    fetchPromoWinners(ctrl.signal).then((d) => {
      if (!ctrl.signal.aborted) setDraws(d);
    });
    return () => ctrl.abort();
  }, []);
  return draws;
};

/** The A$10,000 giveaway for a page: undefined while it loads, then the answer
 * (null when it cannot be had). Read once. */
export const useJackpot = () => {
  const [jackpot, setJackpot] = useState<Jackpot | null | undefined>(undefined);
  useEffect(() => {
    const ctrl = new AbortController();
    fetchJackpot(ctrl.signal).then((j) => {
      if (!ctrl.signal.aborted) setJackpot(j);
    });
    return () => ctrl.abort();
  }, []);
  return jackpot;
};

/** The promotion for a page: null while it loads, then the answer. `retry`
 * asks again. */
export const usePromotion = () => {
  const [load, setLoad] = useState<PromotionLoad | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoad(null);
    fetchPromotion(ctrl.signal).then((result) => {
      if (!ctrl.signal.aborted) setLoad(result);
    });
    return () => ctrl.abort();
  }, [attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  return { load, retry };
};
